/**
 * MagicLink.jsx — Public landing page for internal-staff magic-link logins.
 *
 * URL format: /auth/verify?token=<uuid>
 * Type='nutrashore' → GET (validate) then POST (redeem) /api/roch-auth/verify/:token —
 * split MAGICLINK-SCANNER-DETONATION-1 so a scanner/bot GET can no longer consume the
 * link; only an explicit user click on "Continue to sign in" fires the redeeming POST.
 * Stores the resulting session JWT in sessionStorage under 'magicSession'.
 *
 * api.js reads 'magicSession' before falling back to MSAL.
 *
 * The vendor VAP qualification link this page used to also try as a fallback (Type='vendor',
 * TokenMode=vap_scoped/vendor_broad) is retired -- VENDOR-PORTAL-AUTH-2 replaced it with a
 * temp-password + mandatory-TOTP login on the vendor portal itself.
 */

import { useEffect, useState } from 'react';

export default function MagicLink() {
  const [status,    setStatus]    = useState('verifying');
  const [errorMsg,  setErrorMsg]  = useState('');
  const [email,     setEmail]     = useState('');
  const [token,     setToken]     = useState('');
  const [signingIn, setSigningIn] = useState(false);

  useEffect(() => {
    const t = new URLSearchParams(window.location.search).get('token');
    if (!t) {
      setStatus('error');
      setErrorMsg('No login token found in URL. Request a new link from your administrator.');
      return;
    }
    setToken(t);

    // Validate-only -- neither consumes the token nor mints a session.
    fetch(`/api/roch-auth/verify/${encodeURIComponent(t)}`)
      .then(r => r.json())
      .then(data => {
        if (data.valid) { setEmail(data.email); setStatus('ready'); return; }
        setStatus('error');
        setErrorMsg(data.error ?? 'Verification failed.');
      })
      .catch(() => { setStatus('error'); setErrorMsg('Network error. Please try again.'); });
  }, []);

  const handleContinue = () => {
    setSigningIn(true);
    fetch(`/api/roch-auth/verify/${encodeURIComponent(token)}`, { method: 'POST' })
      .then(r => r.json())
      .then(data => {
        if (data.token) {
          sessionStorage.setItem('magicSession', JSON.stringify({ token: data.token, user: data.user }));
          window.location.replace('/dashboard');
          return;
        }
        setSigningIn(false);
        setStatus('error');
        setErrorMsg(data.error ?? 'Sign-in failed.');
      })
      .catch(() => {
        setSigningIn(false);
        setStatus('error');
        setErrorMsg('Network error. Please try again.');
      });
  };

  const cfg     = JSON.parse(sessionStorage.getItem('tenantConfig') || '{}');
  const name    = cfg.companyName || 'Portal';
  const primary = cfg.primaryColor || '#062722'; // hex-ok — tenant primaryColor fallback, no matching semantic var

  const cardStyle = { display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: 'var(--color-bg-primary)' };
  const boxStyle  = { textAlign: 'center', padding: '3rem', background: 'var(--color-bg-secondary)', borderRadius: '0.75rem', boxShadow: '0 4px 24px rgba(6,39,34,0.12)', maxWidth: '360px', width: '100%', border: '1px solid var(--color-border)' };

  if (status === 'verifying') {
    return (
      <div style={cardStyle}>
        <div style={boxStyle}>
          <div style={{ fontWeight: 800, fontSize: '1.375rem', color: primary, marginBottom: '0.25rem' }}>{name}</div>
          <div style={{ fontSize: '0.7rem', color: 'var(--color-accent)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '2rem' }}>QMS Platform</div>
          <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem', marginBottom: '1.5rem' }}>Verifying your login link&hellip;</div>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <div style={{ width: '2rem', height: '2rem', border: '3px solid var(--color-border)', borderTopColor: 'var(--color-accent)', borderRadius: '50%', animation: 'ml-spin 0.8s linear infinite' }} />
          </div>
          <style>{`@keyframes ml-spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  if (status === 'ready') {
    return (
      <div style={cardStyle}>
        <div style={boxStyle}>
          <div style={{ fontWeight: 800, fontSize: '1.375rem', color: primary, marginBottom: '0.25rem' }}>{name}</div>
          <div style={{ fontSize: '0.7rem', color: 'var(--color-accent)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '2rem' }}>QMS Platform</div>
          <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem', marginBottom: '0.375rem' }}>Sign in as</div>
          <div style={{ color: 'var(--color-text-primary)', fontSize: '1rem', fontWeight: 700, marginBottom: '1.75rem', wordBreak: 'break-word' }}>{email}</div>
          <button
            onClick={handleContinue}
            disabled={signingIn}
            style={{
              width: '100%',
              padding: '0.75rem 1rem',
              background: 'var(--color-accent)',
              color: 'var(--color-accent-btn-text)',
              border: 'none',
              borderRadius: '0.5rem',
              fontSize: '0.875rem',
              fontWeight: 700,
              cursor: signingIn ? 'default' : 'pointer',
              opacity: signingIn ? 0.7 : 1,
            }}
          >
            {signingIn ? 'Signing in…' : 'Continue to sign in'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={cardStyle}>
      <div style={boxStyle}>
        <div style={{ fontWeight: 800, fontSize: '1.375rem', color: primary, marginBottom: '0.25rem' }}>Sign-in Failed</div>
        <div style={{ fontSize: '0.7rem', color: 'var(--color-accent)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '1.5rem' }}>QMS Platform</div>
        <div style={{ background: 'var(--color-danger-light)', border: '1px solid var(--color-danger)', borderRadius: '0.5rem', padding: '0.75rem 1rem', color: 'var(--color-danger)', fontSize: '0.875rem', marginBottom: '1.25rem' }}>
          {errorMsg}
        </div>
        <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.8rem', margin: 0 }}>Contact your administrator to request a new login link.</p>
      </div>
    </div>
  );
}
